import {
  useCreateAndExtractDocumentMutation,
  useGetDocumentLazyQuery,
  useRequestDocumentCorrectionMutation,
  useSetCanonicalExtractionMutation,
  ExtractionAttemptFieldsFragment,
} from '@/dealers/_gen/gql'
import { trackUploadQuote } from '@/gf/modules/Analytics'
import { useRollbar } from '@rollbar/react'
import { Maybe } from '@/types'
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { FormValues } from '../QuoteBuilder/useForm'
import { QuoteUploadProps } from './QuoteUploadButton'

export type UploadStage = 'processing' | 'error' | 'finished' | null

// Line item shape from extraction result
type ExtractedLineItem = {
  part_number?: string
  alt_part_numbers?: unknown
  description?: string
  quantity?: number
  unit_price?: number | null
}

type DocumentUploadContextValue = {
  uploadStage: UploadStage
  error: Maybe<string>
  lineItems: FormValues['items']
  // Document-level fields from AiAssistantStructured. `null` means the
  // extractor did not find one; `0` means the doc had a zero amount. Consumers
  // must treat these distinctly — overriding form defaults only when non-null.
  shippingCost: Maybe<number>
  taxCost: Maybe<number>
  attachmentUrls: string[]
  isReady: boolean
  rfqId: Maybe<string>
  documentId: Maybe<string>
  assistantModalOpen: boolean
  correctionLoading: boolean
  correctionError: Maybe<string>
  pendingCorrection: Maybe<ExtractionAttemptFieldsFragment>
  // True if an upload completed successfully in this session (persists after modal dismiss)
  hasUploadedThisSession: boolean
  // Increments whenever lineItems are updated (for forcing form remounts)
  lineItemsVersion: number
  onDismiss: () => void
  onTryAiAssistant: () => void
  onCloseAssistantModal: () => void
  onUploadStarted: QuoteUploadProps['onUploadStarted']
  onUploadComplete: QuoteUploadProps['onUploadComplete']
  onError: QuoteUploadProps['onError']
  onRetry: () => void
  onRequestCorrection: (feedback: string) => Promise<void>
  onAcceptCorrection: () => Promise<void>
  onRejectCorrection: () => void
}

const initialValue: DocumentUploadContextValue = {
  uploadStage: null,
  error: null,
  lineItems: [],
  shippingCost: null,
  taxCost: null,
  attachmentUrls: [],
  isReady: false,
  rfqId: null,
  documentId: null,
  assistantModalOpen: false,
  correctionLoading: false,
  correctionError: null,
  pendingCorrection: null,
  hasUploadedThisSession: false,
  lineItemsVersion: 0,
  onDismiss: () => undefined,
  onTryAiAssistant: () => undefined,
  onCloseAssistantModal: () => undefined,
  onUploadStarted: () => undefined,
  onUploadComplete: (_files: { name: string; url: string }[]) => Promise.resolve(undefined),
  onError: (_message: string) => undefined,
  onRetry: () => undefined,
  onRequestCorrection: async () => undefined,
  onAcceptCorrection: async () => undefined,
  onRejectCorrection: () => undefined,
}

export const DocumentUploadContext = React.createContext<DocumentUploadContextValue>(initialValue)

// Normalize price from various extractor formats to dollars
// - LlamaQuote returns Money struct: {amount: cents, currency: "USD"}
// - AiAssistant returns integer cents
const normalizePrice = (price: unknown): number | null => {
  if (price == null) return null

  // Handle Money-like objects (from LlamaQuote)
  if (typeof price === 'object' && 'amount' in price) {
    const amount = (price as { amount: number }).amount
    return typeof amount === 'number' ? amount / 100 : null
  }

  // Handle integer cents (from AiAssistant)
  if (typeof price === 'number') {
    return price / 100
  }

  return null
}

// Helper to convert extraction result to form line items
const extractionToLineItems = (result: unknown): FormValues['items'] => {
  if (!result || typeof result !== 'object') return []

  const extractedResult = result as { line_items?: ExtractedLineItem[] }
  const lineItems = extractedResult.line_items

  if (!Array.isArray(lineItems)) return []

  return lineItems.map((li) => ({
    id: null,
    partNumber: li.part_number || '',
    // Defensively trim + drop empties + dedupe — extractor occasionally emits
    // whitespace-only or repeated entries that would render as blank or
    // duplicate chips in the form.
    altPartNumbers: Array.isArray(li.alt_part_numbers)
      ? Array.from(
          new Set(
            li.alt_part_numbers
              .filter((s): s is string => typeof s === 'string')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          )
        )
      : [],
    inStock: true,
    quantity: li.quantity || 1,
    unitPrice: normalizePrice(li.unit_price),
    description: li.description || '',
    externalId: null,
    taskNumber: null,
    suggestion: false,
    availabilityDate: null,
    rfqPartId: null,
    rfqPartNumber: null,
  }))
}

// Pull `shipping_cost` / `tax_cost` (integer cents from AiAssistantStructured)
// out of the extraction result as dollar amounts. `null` flows through as
// `null` and means "extractor did not find this field" — distinct from `0`,
// which means the doc had a zero amount. Callers should preserve that
// distinction (only override form defaults when the value is non-null).
const extractShippingTax = (
  result: unknown
): { shippingCost: Maybe<number>; taxCost: Maybe<number> } => {
  if (!result || typeof result !== 'object') {
    return { shippingCost: null, taxCost: null }
  }
  const r = result as { shipping_cost?: unknown; tax_cost?: unknown }
  return {
    shippingCost: normalizePrice(r.shipping_cost),
    taxCost: normalizePrice(r.tax_cost),
  }
}

const POLL_INTERVAL = 2000
const CORRECTION_TIMEOUT_MS = 90_000

const DocumentUploadProvider = ({
  children,
  rfqId,
  existingDocumentId,
}: {
  children?: React.ReactNode
  rfqId: Maybe<string>
  existingDocumentId?: Maybe<string>
}) => {
  const [uploadStage, setUploadStage] = useState<UploadStage>(initialValue.uploadStage)
  const [error, setError] = useState<Maybe<string>>(initialValue.error)
  const [lineItems, setLineItems] = useState<FormValues['items']>(initialValue.lineItems)
  const [shippingCost, setShippingCost] = useState<Maybe<number>>(initialValue.shippingCost)
  const [taxCost, setTaxCost] = useState<Maybe<number>>(initialValue.taxCost)
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>(initialValue.attachmentUrls)
  // Initialize with existing document ID from RFQ query (for page refresh case)
  const [documentId, setDocumentId] = useState<Maybe<string>>(existingDocumentId ?? null)
  const [assistantModalOpen, setAssistantModalOpen] = useState(false)
  const [correctionLoading, setCorrectionLoading] = useState(false)
  const [correctionError, setCorrectionError] = useState<Maybe<string>>(null)
  const [pendingCorrection, setPendingCorrection] =
    useState<Maybe<ExtractionAttemptFieldsFragment>>(null)
  // Persists after modal dismiss to keep AI Assistant callout visible
  const [hasUploadedThisSession, setHasUploadedThisSession] = useState(false)
  // Increments whenever lineItems are updated (for forcing form remounts)
  const [lineItemsVersion, setLineItemsVersion] = useState(0)

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const correctionPollRef = useRef<NodeJS.Timeout | null>(null)
  const correctionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  const uploadAttemptRef = useRef(0)
  const lastUploadedFilesRef = useRef<{ name: string; url: string }[]>([])

  const rollbar = useRollbar()
  const [createAndExtract] = useCreateAndExtractDocumentMutation()
  const [getDocument] = useGetDocumentLazyQuery()
  const [requestCorrection] = useRequestDocumentCorrectionMutation()
  const [setCanonical] = useSetCanonicalExtractionMutation()

  // Clean up polling on unmount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      if (correctionPollRef.current) {
        clearTimeout(correctionPollRef.current)
      }
      if (correctionTimeoutRef.current) {
        clearTimeout(correctionTimeoutRef.current)
      }
    }
  }, [])

  // Sync documentId when existingDocumentId becomes available (after RFQ query loads)
  useEffect(() => {
    if (existingDocumentId && !documentId) {
      setDocumentId(existingDocumentId)
    }
  }, [existingDocumentId, documentId])

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  // Poll document for extraction status
  const pollDocument = useCallback(
    async (docId: string, attemptId: number) => {
      try {
        const { data } = await getDocument({
          variables: { id: docId },
          fetchPolicy: 'network-only',
        })

        // Discard stale response if a new upload attempt started while in flight
        if (uploadAttemptRef.current !== attemptId) return

        const doc = data?.document

        if (!doc?.canonicalExtraction) return

        const extraction = doc.canonicalExtraction

        if (extraction.status === 'succeeded') {
          stopPolling()
          const items = extractionToLineItems(extraction.result)

          if (items.length === 0) {
            rollbar.warning('Quote upload: polling extraction succeeded but 0 line items', {
              rfqId,
              documentId: docId,
            })
            setUploadStage('error')
            setError(
              `We were unable to find line items in your uploaded document. Please fill in the form manually or try uploading a different document. (Document: ${docId})`
            )
          } else {
            const { shippingCost: extractedShipping, taxCost: extractedTax } = extractShippingTax(
              extraction.result
            )
            setLineItems(items)
            setShippingCost(extractedShipping)
            setTaxCost(extractedTax)
            setLineItemsVersion((v) => v + 1)
            setUploadStage('finished')
            setHasUploadedThisSession(true)
            trackUploadQuote(doc.sourceUrls, items.length)
          }
        } else if (extraction.status === 'failed') {
          stopPolling()
          rollbar.error('Quote upload: polling extraction failed', {
            rfqId,
            documentId: docId,
            error: extraction.error,
          })
          setUploadStage('error')
          setError(
            extraction.error
              ? `${extraction.error} (Document: ${docId})`
              : `Extraction failed. Please try again. (Document: ${docId})`
          )
        }
        // If still processing, keep polling
      } catch (err) {
        console.error('Error polling document:', err)
      }
    },
    [getDocument, stopPolling]
  )

  const onUploadStarted = () => {
    setUploadStage('processing')
    setError(null)
    setLineItems([])
    setShippingCost(null)
    setTaxCost(null)
    setAttachmentUrls([])
    setDocumentId(null)
    setPendingCorrection(null)
    setCorrectionError(null)
    setHasUploadedThisSession(false)
    lastUploadedFilesRef.current = []
  }

  const onError = (message: string) => {
    setUploadStage('error')
    setError(message)
    stopPolling()
  }

  const onDismiss = () => {
    if (uploadStage !== 'processing') {
      setUploadStage(null)
      setError(null)
    }
  }

  const onTryAiAssistant = () => {
    setAssistantModalOpen(true)
    setCorrectionError(null)
    setPendingCorrection(null)
  }

  const onCloseAssistantModal = () => {
    setAssistantModalOpen(false)
    setCorrectionError(null)
    setPendingCorrection(null)
  }

  const onUploadComplete = useCallback(
    async (uploadedFiles: { name: string; url: string }[]) => {
      try {
        stopPolling()
        setError(null)
        setUploadStage('processing')
        const currentAttempt = ++uploadAttemptRef.current
        lastUploadedFilesRef.current = uploadedFiles

        // Filter out any files without valid URLs (upload may have failed)
        const urls = uploadedFiles.map((file) => file.url).filter((url) => url && url.length > 0)

        // Validate we have at least one URL before proceeding
        if (urls.length === 0) {
          rollbar.error('Quote upload: no valid URLs after S3 upload', {
            rfqId,
            fileCount: uploadedFiles.length,
            fileNames: uploadedFiles.map((f) => f.name),
          })
          onError('File upload failed. Please check your internet connection and try again.')
          return
        }

        setAttachmentUrls(urls)

        // Determine source type from file extension (handle URLs with query strings)
        const firstUrl = urls[0] || ''
        let sourceType: 'pdf' | 'image' = 'image'
        try {
          const urlObj = new URL(firstUrl)
          if (urlObj.pathname.toLowerCase().endsWith('.pdf')) {
            sourceType = 'pdf'
          }
        } catch {
          // Fallback for relative URLs or data/blob URLs
          const pathPart = firstUrl.split('?')[0].split('#')[0]
          if (pathPart.toLowerCase().endsWith('.pdf')) {
            sourceType = 'pdf'
          }
        }

        const { data } = await createAndExtract({
          variables: {
            sourceUrls: urls,
            sourceType,
            requestForQuoteId: rfqId,
            // The server picks the method: ExtractorRollout.method_for(:sales_hub).
          },
        })

        const doc = data?.createAndExtractDocument

        if (!doc) {
          rollbar.error('Quote upload: createAndExtractDocument returned null', {
            rfqId,
            sourceUrls: urls,
            sourceType,
          })
          onError('Failed to create document. Please try again.')
          return
        }

        setDocumentId(doc.id)

        // Check if extraction already completed (synchronous case)
        if (doc.canonicalExtraction?.status === 'succeeded') {
          const items = extractionToLineItems(doc.canonicalExtraction.result)

          if (items.length === 0) {
            rollbar.warning('Quote upload: extraction succeeded but 0 line items', {
              rfqId,
              documentId: doc.id,
              sourceUrls: urls,
              sourceType,
            })
            onError(
              `We were unable to find line items in your uploaded document. Please fill in the form manually or try uploading a different document. (Document: ${doc.id})`
            )
          } else {
            const { shippingCost: extractedShipping, taxCost: extractedTax } = extractShippingTax(
              doc.canonicalExtraction.result
            )
            setLineItems(items)
            setShippingCost(extractedShipping)
            setTaxCost(extractedTax)
            setLineItemsVersion((v) => v + 1)
            setUploadStage('finished')
            setHasUploadedThisSession(true)
            trackUploadQuote(urls, items.length)
          }
        } else if (doc.canonicalExtraction?.status === 'failed') {
          rollbar.error('Quote upload: extraction failed', {
            rfqId,
            documentId: doc.id,
            error: doc.canonicalExtraction.error,
          })
          onError(
            doc.canonicalExtraction.error
              ? `${doc.canonicalExtraction.error} (Document: ${doc.id})`
              : `Extraction failed. Please try again. (Document: ${doc.id})`
          )
        } else {
          // Start polling for async extraction. No client-side timeout: a
          // backend Oban cron sweeper (Gf.Documents.ExtractionStalenessWorker)
          // flips dead attempts to `failed` after 10 min, so this loop is
          // guaranteed to terminate on a real terminal status. Without the
          // sweeper, this would spin forever on a stranded `processing` row.
          pollIntervalRef.current = setInterval(() => {
            pollDocument(doc.id, currentAttempt)
          }, POLL_INTERVAL)
        }
      } catch (err) {
        rollbar.error('Quote upload: unexpected error', {
          rfqId,
          error: err instanceof Error ? err.message : String(err),
        })
        onError(
          'We were unable to process your document. Please fill in the form manually or try uploading a different document.'
        )
        console.error(err)
      }
    },
    [rfqId, createAndExtract, pollDocument, stopPolling]
  )

  // Request AI correction
  const onRequestCorrection = useCallback(
    async (feedback: string) => {
      if (!documentId) {
        setCorrectionError('No document to correct')
        return
      }

      setCorrectionLoading(true)
      setCorrectionError(null)
      setPendingCorrection(null)

      try {
        const { data } = await requestCorrection({
          variables: {
            documentId,
            feedback,
          },
        })

        const attempt = data?.requestDocumentCorrection

        if (!attempt) {
          setCorrectionError(
            `Failed to request correction. Please try again. (Document: ${documentId})`
          )
          return
        }

        if (attempt.status === 'succeeded') {
          setPendingCorrection(attempt)
        } else if (attempt.status === 'failed') {
          setCorrectionError(
            attempt.error
              ? `${attempt.error} (Document: ${documentId})`
              : `Correction failed. Please try again. (Document: ${documentId})`
          )
        } else {
          // Poll for completion with timeout
          const stopCorrectionPolling = () => {
            if (correctionPollRef.current) {
              clearTimeout(correctionPollRef.current)
              correctionPollRef.current = null
            }
            if (correctionTimeoutRef.current) {
              clearTimeout(correctionTimeoutRef.current)
              correctionTimeoutRef.current = null
            }
          }

          const pollCorrection = async () => {
            if (!mountedRef.current) return

            const { data: docData } = await getDocument({
              variables: { id: documentId },
              fetchPolicy: 'network-only',
            })

            if (!mountedRef.current) return

            // Find the correction attempt in extraction_attempts
            const attempts = docData?.document?.extractionAttempts || []
            const correction = attempts.find((a) => a.id === attempt.id)

            if (correction?.status === 'succeeded') {
              stopCorrectionPolling()
              setPendingCorrection(correction)
              setCorrectionLoading(false)
            } else if (correction?.status === 'failed') {
              stopCorrectionPolling()
              setCorrectionError(
                correction.error
                  ? `${correction.error} (Document: ${documentId})`
                  : `Correction failed. Please try again. (Document: ${documentId})`
              )
              setCorrectionLoading(false)
            } else {
              // Keep polling - clear previous timeout first
              if (correctionPollRef.current) {
                clearTimeout(correctionPollRef.current)
              }
              correctionPollRef.current = setTimeout(pollCorrection, POLL_INTERVAL)
            }
          }

          // Start correction timeout
          correctionTimeoutRef.current = setTimeout(() => {
            if (correctionPollRef.current) {
              stopCorrectionPolling()
              setCorrectionError(
                `Correction is taking longer than expected. Please try again. (Document: ${documentId})`
              )
              setCorrectionLoading(false)
            }
          }, CORRECTION_TIMEOUT_MS)

          pollCorrection()
          return // Don't set loading to false yet
        }
      } catch (err) {
        setCorrectionError(err instanceof Error ? err.message : 'An error occurred')
      }

      setCorrectionLoading(false)
    },
    [documentId, requestCorrection, getDocument]
  )

  // Accept a pending correction as the new canonical extraction
  const onAcceptCorrection = useCallback(async () => {
    if (!documentId || !pendingCorrection) return

    try {
      const { data } = await setCanonical({
        variables: {
          documentId,
          extractionAttemptId: pendingCorrection.id,
        },
      })

      const doc = data?.setCanonicalExtraction

      if (doc?.canonicalExtraction?.result) {
        const items = extractionToLineItems(doc.canonicalExtraction.result)
        if (items.length === 0) {
          setCorrectionError(
            'The correction produced no line items. Please try again with different feedback.'
          )
          return
        }
        const { shippingCost: extractedShipping, taxCost: extractedTax } = extractShippingTax(
          doc.canonicalExtraction.result
        )
        setLineItems(items)
        setShippingCost(extractedShipping)
        setTaxCost(extractedTax)
        setLineItemsVersion((v) => v + 1)
        // Don't close modal here - let the caller decide (Apply & Continue vs Apply & Close)
      }

      setPendingCorrection(null)
    } catch (err) {
      setCorrectionError(err instanceof Error ? err.message : 'Failed to apply correction')
    }
  }, [documentId, pendingCorrection, setCanonical])

  // Reject a pending correction (discard it)
  const onRejectCorrection = useCallback(() => {
    setPendingCorrection(null)
    setCorrectionError(null)
  }, [])

  // Retry the last upload
  const onRetry = useCallback(() => {
    const files = lastUploadedFilesRef.current
    if (files.length > 0) {
      onUploadComplete(files)
    }
  }, [onUploadComplete])

  const value = useMemo<DocumentUploadContextValue>(
    () => ({
      uploadStage,
      lineItems,
      shippingCost,
      taxCost,
      attachmentUrls,
      error,
      isReady: uploadStage === 'finished' && lineItems.length > 0,
      rfqId,
      documentId,
      assistantModalOpen,
      correctionLoading,
      correctionError,
      pendingCorrection,
      hasUploadedThisSession,
      lineItemsVersion,
      onUploadStarted,
      onUploadComplete,
      onError,
      onRetry,
      onDismiss,
      onTryAiAssistant,
      onCloseAssistantModal,
      onRequestCorrection,
      onAcceptCorrection,
      onRejectCorrection,
    }),
    [
      lineItems,
      shippingCost,
      taxCost,
      attachmentUrls,
      error,
      uploadStage,
      rfqId,
      documentId,
      assistantModalOpen,
      correctionLoading,
      correctionError,
      pendingCorrection,
      hasUploadedThisSession,
      lineItemsVersion,
      onUploadComplete,
      onRetry,
      onRequestCorrection,
      onAcceptCorrection,
      onRejectCorrection,
    ]
  )

  return <DocumentUploadContext.Provider value={value}>{children}</DocumentUploadContext.Provider>
}

export default DocumentUploadProvider
